import React, { useRef } from 'react';
import { animated } from '@react-spring/web';
import { ISubscriptionTile } from '@dazn/lp-web/src/types';
import { tileTypeConfig } from '@dazn/lp-web/src/utils/tileConfig';
import { useSyncRefHeight } from '@dazn/lp-web/src/hooks/useSyncHeight';
import { useTileTransitions } from '@dazn/lp-web/src/hooks/useTransitions';
import SimpleResponsiveImage from '@dazn/lp-web/src/components/Images/SimpleResponsiveImage/SimpleResponsiveImage';
import styles from '@dazn/lp-web/src/components/SubscriptionsRail/subscriptions-rail.module.css';
import { triggerElementClick } from '@dazn/lp-web/src/analytics/fe-events';
import { elementClickEventTypes } from '@dazn/lp-web/src/analytics/fe-events/elementClick';
import { useLandingPagesContext } from '@dazn/lp-web/src/contexts/LandingPageContext';
import isQueryParamTrue from '../../utils/isQueryparamTrue';

const SubscriptionsTile: React.FC<{
    tile: ISubscriptionTile;
    index: number;
    slidesPerView: number;
    disableAnimation: boolean;
}> = ({ tile, index, slidesPerView, disableAnimation = false }) => {
    const [refView, springs] = useTileTransitions();
    const { getPromoCodeByEntSetId } = useLandingPagesContext();

    const animations = (() => {
        if (index >= slidesPerView || isQueryParamTrue('seo')) return {};
        return {
            style: springs,
            ref: refView
        };
    })();

    const logosRef = useRef<HTMLImageElement | null>(null);
    const descriptionRef = useRef<HTMLDivElement | null>(null);
    const buttoRef = useRef<HTMLButtonElement | null>(null);

    useSyncRefHeight(
        [
            ['LogoContainer', logosRef],
            ['Description', descriptionRef],
            ['Button', buttoRef]
        ],
        []
    );

    const handleTileButtonClick = () => {
        triggerElementClick({
            section: elementClickEventTypes.BODY,
            itemId: `${tile?.button?.entitlementSetId}_lp_subscribe_cta`,
            resourceString: tile?.button?.label || ''
        });

        if (tile.button?.action) {
            const entSetId = tile?.button?.entitlementSetId;
            const promoCode = getPromoCodeByEntSetId(entSetId);

            tile.button?.action({ promoCode });
        }
    };

    return (
        <animated.article {...(disableAnimation ? {} : animations)}>
            <div className={styles.subscriptionTile}>
                <SimpleResponsiveImage
                    imageUrl={`${tile?.image.src}`}
                    aspectRatio={tileTypeConfig.PORTRAIT.aspectRatio}
                />
                <div className={styles.container}>
                    {tile?.logo?.src && (
                        <img
                            className={styles.logo}
                            src={tile?.logo.src}
                            alt="logo"
                            ref={logosRef}
                        />
                    )}
                    {tile?.description && (
                        <p ref={descriptionRef} className={styles.description}>
                            {tile.description}
                        </p>
                    )}
                    {tile?.button?.label &&
                        (isQueryParamTrue('seo') ? (
                            <a
                                href={tile.button?.navigationLink}
                                className={styles.button}
                            >
                                {tile.button.label}
                            </a>
                        ) : (
                            <button
                                ref={buttoRef}
                                className={styles.button}
                                onClick={handleTileButtonClick}
                            >
                                {tile.button.label}
                            </button>
                        ))}
                </div>
            </div>
        </animated.article>
    );
};

export default SubscriptionsTile;
